import { Autocomplete } from "components/Form/components/Autocomplete/Autocomplete";
import { Text } from "components/Form/components/Text/Text";
import {
    ImportOverride,
    ImportPreview,
    ImportProblem,
    ImportRecordRow,
} from "containers/Resources/components/ImportSessionModal/useImportSession";
import { ReactElement } from "react";

import "./ImportSessionModal.scss";

/** Stable key identifying the problem a resolution choice belongs to. */
export const problemKey = (
    recordType: string | null,
    recordName: string | null
): string => `${recordType ?? ""}::${recordName ?? ""}`;

/**
 * Which stage of the import the preview is rendering for. The SAME records list
 * stays on screen across all three (Margot, 2026-10-15: "continue to show the
 * same dialogue with the list of records before the import, during the import
 * ... and after the import also, with ... green checkmarks"):
 * - `review`   — before apply: interactive resolution pickers are shown.
 * - `applying` — during apply: the list is frozen (no pickers); the busy state
 *                is conveyed by the modal's Import-button spinner, not a full-
 *                screen loader that hides the list.
 * - `done`     — after a successful apply: the records table gains a Result
 *                column (Imported / Skipped per row) and the summary reads
 *                "Import complete", so the user sees exactly which of the records
 *                they were shown got imported — instead of an aggregate-only
 *                count they cannot reconcile with the list.
 */
export type ImportPreviewMode = "review" | "applying" | "done";

interface ImportSessionPreviewProps {
    preview: ImportPreview;
    /** Map of problemKey -> chosen resolution id. */
    resolutions: Record<string, string>;
    /** Called when the user picks a resolution for a problem. */
    onResolutionChange: (problem: ImportProblem, resolutionId: string) => void;
    /** Map of problemKey -> the text typed for a "Set a value" resolution. */
    values?: Record<string, string>;
    /** Called when the user types a value for a "Set a value" resolution. */
    onValueChange?: (problem: ImportProblem, value: string) => void;
    /** Import stage this preview renders for (default "review"). */
    mode?: ImportPreviewMode;
}

const recordLabel = (recordType: string | null, recordName: string | null): string => {
    if (!recordType && !recordName) {
        return "";
    }
    if (recordType && recordName) {
        return `${recordType} "${recordName}"`;
    }
    return recordType ?? recordName ?? "";
};

/**
 * The record a problem is shown against. A sub-record problem (e.g. an object
 * template attribute) also names the record it belongs to.
 */
const problemRecordLabel = (problem: ImportProblem): string => {
    const own = recordLabel(problem.record_type, problem.record_name);
    const parent = recordLabel(
        problem.parent_record_type ?? null,
        problem.parent_record_name ?? null
    );
    if (own && parent) {
        return `${own} in ${parent}`;
    }
    return own || parent;
};


/**
 * Human-readable labels for the backend import problem-code taxonomy
 * (`_classify_preflight_problem` in resources/import_.py). Unmapped codes fall
 * back to a humanized form of the raw snake_case code, so a new backend code
 * still renders readably (e.g. "some_new_code" -> "Some new code") rather than
 * leaking raw snake_case to the user.
 */
const PROBLEM_CODE_LABELS: Record<string, string> = {
    schedule_enabled_locked: "Schedule is enabled",
    missing_reference: "Missing reference",
    dependency_unresolved: "Blocked by another problem",
    unresolved_identity_reference: "Unresolved identity reference",
    dependency_cycle: "Dependency cycle",
    duplicate_import: "Duplicate import",
    field_not_importable: "Field not importable",
    name_conflict: "Name already taken",
    invalid_name: "Invalid name",
    invalid_value: "Invalid value",
    record_readonly: "Target is read-only",
    immutable_field: "Field cannot be changed",
    build_error: "Build error",
    unknown: "Unresolved problem",
};

const problemCodeLabel = (code: string): string => {
    const mapped = PROBLEM_CODE_LABELS[code];
    if (mapped) {
        return mapped;
    }
    const humanized = code.replace(/_/g, " ").trim();
    return humanized ? humanized.charAt(0).toUpperCase() + humanized.slice(1) : code;
};

/** Resolution id: import the record with a user-supplied value for one field. */
export const CHANGE_VALUE = "change-value";

/**
 * Fields whose "Set a value" input is always imported as text. Any other field
 * reads the typed value as JSON when it parses (true/false, null, numbers,
 * lists, objects) and as text otherwise — e.g. `is_readonly` needs a boolean.
 */
const TEXT_VALUE_FIELDS = new Set<string>(["name", "repository_name", "description"]);

/** The field a problem's change-value resolution replaces, or null. */
export const changeValueField = (problem: ImportProblem): string | null =>
    problem.resolutions.find((resolution) => resolution.id === CHANGE_VALUE)?.field ??
    null;

/** The value to send for the text typed into a "Set a value" input. */
export const parseChangeValue = (field: string, raw: string): unknown => {
    if (TEXT_VALUE_FIELDS.has(field)) {
        return raw;
    }
    try {
        return JSON.parse(raw.trim());
    } catch {
        return raw;
    }
};

/** Whether a "Set a value" input still needs a value before importing. */
export const isMissingChangeValue = (raw: string | undefined): boolean =>
    (raw ?? "").trim() === "";

/**
 * Resolution ids the v1 modal actually offers. The backend proposes a fuller
 * per-code resolution taxonomy (`_classify_preflight_problem` in
 * resources/import_.py) — e.g. `relink-to-existing` / `provide-name` for a
 * missing reference — but only `skip` / `skip-dependent` (backend
 * `_resolution_skip_keys`), `change-value` (backend `_resolution_overrides`) and
 * `disable-reenable` for an enabled record (backend
 * `_resolution_disable_reenable_keys`) are consumed at apply.
 * The rest are offered-but-still-block, pending their own backend follow-ups
 * and the remap table (operator-task-import-remap-console-ui). Offering a
 * resolution that silently does nothing reads as broken to a user, so v1 shows
 * only the functional ones (Stefan, 2026-10-05, option A on
 * operator-task-fe-import-session-modal). Add each id back here as its backend
 * lands so the modal starts offering it.
 */
export const FUNCTIONAL_RESOLUTION_IDS = new Set<string>([
    "skip",
    "skip-dependent",
    CHANGE_VALUE,
    "disable-reenable",
]);

/** Resolutions that leave their record out of the import. */
const SKIP_RESOLUTION_IDS = new Set<string>(["skip", "skip-dependent"]);

/**
 * Whether Import would write at least one record. A record does not count when
 * the preflight already marks it "skip", or when the resolution chosen for its
 * problem skips it. When nothing is left, the Import button is disabled: a
 * click could only report "0 records imported" (Margot, 2026-11-02).
 */
/**
 * Whether a record would be imported: not skipped by the preview (action
 * "skip", e.g. its project is missing) and not skipped by the resolution the
 * user picked for its problem.
 */
const isImported = (record: ImportRecordRow, resolutions: Record<string, string>): boolean => {
    if (record.action === "skip") {
        return false;
    }
    const resolution = resolutions[problemKey(record.record_type, record.name)];
    return !(resolution && SKIP_RESOLUTION_IDS.has(resolution));
};

/** Number of records the import would write (see isImported). */
export const countRecordsToImport = (
    records: ImportRecordRow[],
    resolutions: Record<string, string>
): number => records.filter((record) => isImported(record, resolutions)).length;

export const wouldImportAnything = (
    records: ImportRecordRow[],
    resolutions: Record<string, string>
): boolean => records.some((record) => isImported(record, resolutions));

/** The subset of a problem's resolutions the v1 modal actually offers. */
export const functionalResolutions = (
    problem: ImportProblem
): ImportProblem["resolutions"] =>
    problem.resolutions.filter((resolution) =>
        FUNCTIONAL_RESOLUTION_IDS.has(resolution.id)
    );

/**
 * Record types the "Create missing target locations" option can synthesise from
 * a bare reference (backend `create_missing_locations`, restricted to
 * project/bundle — other types cannot be created from a reference alone).
 */
export const LOCATION_RECORD_TYPES = new Set<string>(["project", "bundle"]);

/**
 * Whether a problem is a missing *location* (project/bundle) that the "Create
 * missing target locations" option would resolve. The backend records a
 * referenced-only project/bundle that is absent on the target as a
 * `missing_reference` problem attributed to that location node, so its
 * `record_type` is the location type; and `create_missing_locations` only ever
 * creates `project`/`bundle` — so this pair identifies exactly the problems the
 * option fixes. Used to (a) show the option only when it is relevant and (b)
 * name the specific locations it would create, so the user can see WHICH problem
 * the option resolves (Margot, 2026-10-15, operator-task-fe-import-session-modal).
 */
export const isLocationProblem = (problem: ImportProblem): boolean =>
    problem.code === "missing_reference" &&
    problem.record_type != null &&
    LOCATION_RECORD_TYPES.has(problem.record_type);

/** The distinct missing locations across a problem set (dedup by type + name). */
export const locationProblems = (
    problems: ImportProblem[]
): { type: string; name: string }[] => {
    const seen = new Set<string>();
    const out: { type: string; name: string }[] = [];
    problems.forEach((problem) => {
        if (!isLocationProblem(problem)) {
            return;
        }
        const type = problem.record_type as string;
        const name = problem.record_name ?? "";
        const dedupeKey = `${type}::${name}`;
        if (seen.has(dedupeKey)) {
            return;
        }
        seen.add(dedupeKey);
        out.push({ type, name });
    });
    return out;
};

/** Phrase a location set for a label, e.g. `project "X", bundle "Y"`. */
export const describeLocations = (
    locations: { type: string; name: string }[]
): string =>
    locations.map((location) => `${location.type} "${location.name}"`).join(", ");

/**
 * Auto-apply the single functional resolution of every no-choice problem.
 *
 * A problem with exactly ONE functional resolution offers no real choice, so the
 * preview states its consequence as plain text instead of a one-item dropdown
 * (Margot, 2026-10-08). For that stated consequence to be TRUE, the single
 * resolution must actually be applied on Import — otherwise the problem stays
 * blocking and the import fails. This merges those auto-selections into the
 * user's resolution map WITHOUT overwriting a key already chosen (a manual pick
 * on a multi-option problem is preserved). Returns the SAME `current` reference
 * when nothing changed, so a caller effect can skip a redundant state update.
 */
export const withAutoResolutions = (
    problems: ImportProblem[],
    current: Record<string, string>
): Record<string, string> => {
    let changed = false;
    const next = { ...current };
    problems.forEach((problem) => {
        const offered = functionalResolutions(problem);
        if (offered.length !== 1) {
            return;
        }
        const key = problemKey(problem.record_type, problem.record_name);
        if (!(key in next)) {
            next[key] = offered[0].id;
            changed = true;
        }
    });
    return changed ? next : current;
};

/**
 * When a problem has exactly ONE functional resolution there is no choice to
 * make, so the modal states the consequence as a plain sentence instead of a
 * one-item dropdown (Margot, 2026-10-08, operator-task-fe-import-session-modal):
 * a single-option dropdown reads as "pick something" when nothing can be picked.
 * The single resolution is auto-applied by the modal, so the sentence must tell
 * the TRUTH about what will happen when the user imports. Keyed by resolution id;
 * an unmapped single resolution falls back to its own label.
 */
const SINGLE_RESOLUTION_CONSEQUENCE: Record<string, string> = {
    "skip-dependent":
        "The record(s) that depend on this reference will be skipped — they will not be imported.",
    skip: "This record will be skipped — the target is left unchanged and this record will not be imported.",
};

/** One "Changed values" line of the done view. */
export const describeOverride = (entry: ImportOverride): string => {
    const record = recordLabel(entry.record_type, entry.record_name);
    if (entry.field === "name" && entry.to !== undefined) {
        return `${record} imported as "${String(entry.to)}"`;
    }
    return `${record}: ${entry.field} set to the value you entered`;
};

interface ChangeValueInputProps {
    problem: ImportProblem;
    inputKey: string;
    field: string;
    value: string;
    readOnly: boolean;
    onChange: (value: string) => void;
}

/** The "Set a value" input under a problem whose change-value was chosen. */
const ChangeValueInput = ({
    problem,
    inputKey,
    field,
    value,
    readOnly,
    onChange,
}: ChangeValueInputProps): ReactElement => {
    const inputName = `change-value-${inputKey}`;
    return (
        <div className="import-session-preview__change-value">
            <label
                className="import-session-preview__change-value-label"
                htmlFor={inputName}
            >
                New value for{" "}
                <code>{field}</code>
            </label>
            <div className="input import-session-preview__change-value-input">
                <Text
                    name={inputName}
                    attributes={{
                        id: inputName,
                        placeholder:
                            field === "name" ? problem.record_name ?? "" : "",
                        autoComplete: "off",
                    }}
                    inputProps={{
                        value,
                        isReadOnly: readOnly,
                        onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                            onChange(event.target.value),
                    }}
                />
            </div>
            {!readOnly && isMissingChangeValue(value) && (
                <div className="import-session-preview__change-value-hint import-session-preview__change-value-hint--missing">
                    Enter a value to import this record.
                </div>
            )}
            {!readOnly && !TEXT_VALUE_FIELDS.has(field) && (
                <div className="import-session-preview__change-value-hint">
                    true, false, null, numbers and JSON are read as such; anything
                    else is imported as text.
                </div>
            )}
        </div>
    );
};

/**
 * Side-effect-free preview of a staged import: a summary banner, the list of
 * problems the import would hit (each with an inline resolution picker when the
 * backend offers resolutions), and the per-record action table. Rendered inside
 * the ImportSessionModal before apply and re-rendered (shrunken) after each
 * attempt. Modeled on `MigrateDeprecationsPreview` (the migrate-deprecations
 * dry-run preview).
 */
const ImportSessionPreview = ({
    preview,
    resolutions,
    onResolutionChange,
    values = {},
    onValueChange,
    mode = "review",
}: ImportSessionPreviewProps): ReactElement => {
    const { records = [], problems = [], summary, imported } = preview;
    const blocking = summary?.blocking ?? 0;
    const warning = summary?.warning ?? 0;
    const total = summary?.total ?? records.length;
    // Before apply, the banner counts only the records that would really be
    // written; records skipped by the preview or by a chosen resolution are
    // reported separately, so "1 record to import" never shows when the
    // record will be skipped.
    const toImportCount = records.length ? countRecordsToImport(records, resolutions) : total;
    const toSkipCount = records.length ? records.length - toImportCount : 0;

    const isDone = mode === "done";
    // Target locations (project/bundle) created by "Create missing target
    // locations". Only present (and only shown) on a successful apply — listed
    // as their own "Created" rows, named, so the user sees they were created,
    // not imported (Margot, 2026-10-20).
    const createdLocations = isDone ? preview.created_locations ?? [] : [];
    // A missing location is either CREATED (it was absent) or RESTORED (it was in
    // the trash) — report the two separately so the wording is honest (Margot:
    // a provided location should say what actually happened to it).
    const createdCount = createdLocations.filter((location) => !location.restored).length;
    const restoredCount = createdLocations.filter((location) => location.restored).length;
    // After a successful apply, the per-record counts the user can see in the
    // table below (a row's action of "skip" means it was NOT imported; anything
    // else was) — the honest basis for "N imported", matching the list on screen
    // rather than an aggregate the user can't reconcile with it (Margot,
    // 2026-10-15). Fall back to the backend's imported-ids tally only when the
    // result carries no record rows (e.g. a build-phase apply with records: []).
    const importedCount = records.length
        ? records.filter((record) => record.action !== "skip").length
        : Object.values(imported ?? {}).reduce((sum, ids) => sum + ids.length, 0);
    const skippedCount = records.filter((record) => record.action === "skip").length;

    let summaryKind: "clear" | "warning" | "blocking" = "clear";
    if (blocking > 0) {
        summaryKind = "blocking";
    } else if (warning > 0) {
        summaryKind = "warning";
    }

    return (
        <div className="import-session-preview">
            {isDone ? (
                <div className="import-session-preview__summary import-session-preview__summary--clear">
                    <b>Import complete.</b> {importedCount} record
                    {importedCount === 1 ? "" : "s"} imported
                    {skippedCount > 0 && (
                        <>
                            {", "}
                            {skippedCount} skipped
                        </>
                    )}
                    {createdCount > 0 && (
                        <>
                            {", "}
                            {createdCount} location
                            {createdCount === 1 ? "" : "s"} created
                        </>
                    )}
                    {restoredCount > 0 && (
                        <>
                            {", "}
                            {restoredCount} location
                            {restoredCount === 1 ? "" : "s"} restored
                        </>
                    )}
                    .
                </div>
            ) : (
                <div
                    className={`import-session-preview__summary import-session-preview__summary--${summaryKind}`}
                >
                    {toImportCount} record{toImportCount === 1 ? "" : "s"} to import
                    {toSkipCount > 0 && (
                        <>
                            {", "}
                            {toSkipCount} skipped
                        </>
                    )}
                    {blocking > 0 && (
                        <>
                            {" — "}
                            <b>{blocking}</b> blocking problem{blocking === 1 ? "" : "s"}
                        </>
                    )}
                    {warning > 0 && (
                        <>
                            {", "}
                            {warning} warning{warning === 1 ? "" : "s"}
                        </>
                    )}
                    {blocking === 0 && (
                        <> — ready to import. Nothing is applied until you confirm.</>
                    )}
                </div>
            )}

            {problems.length > 0 && (
                <div className="import-session-preview__problems">
                    <div className="import-session-preview__section-title">Problems</div>
                    <ul className="import-session-preview__problem-list">
                        {problems.map((problem, index) => {
                            const key = problemKey(
                                problem.record_type,
                                problem.record_name
                            );
                            const label = problemRecordLabel(problem);
                            // v1: offer only the resolutions that actually apply
                            // today (see FUNCTIONAL_RESOLUTION_IDS). A problem
                            // whose only resolutions are not-yet-functional shows
                            // no picker rather than a silently-inert one.
                            const offeredResolutions = functionalResolutions(problem);
                            // One option = no real choice: state the consequence
                            // as plain text (auto-applied by the modal). >=2 keeps
                            // the picker. (In practice v1 yields 0 or 1.)
                            const singleResolution =
                                offeredResolutions.length === 1
                                    ? offeredResolutions[0]
                                    : null;
                            return (
                                <li
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={`${key}-${index}`}
                                    className={`import-session-preview__problem import-session-preview__problem--${problem.severity}`}
                                >
                                    <div className="import-session-preview__problem-head">
                                        <span
                                            className="import-session-preview__problem-code"
                                            title={problem.code}
                                        >
                                            {problemCodeLabel(problem.code)}
                                        </span>
                                        {label && (
                                            <span className="import-session-preview__problem-record">
                                                {label}
                                            </span>
                                        )}
                                    </div>
                                    <div className="import-session-preview__problem-message">
                                        {problem.message}
                                    </div>
                                    {mode === "review" && isLocationProblem(problem) && (
                                        <div className="import-session-preview__problem-hint">
                                            This {problem.record_type} does not
                                            exist on the target. Enable "Create
                                            missing target locations" below to
                                            create it on import.
                                        </div>
                                    )}
                                    {singleResolution &&
                                        singleResolution.id !== CHANGE_VALUE && (
                                        <div className="import-session-preview__problem-consequence">
                                            {SINGLE_RESOLUTION_CONSEQUENCE[
                                                singleResolution.id
                                            ] ?? singleResolution.label}
                                        </div>
                                    )}
                                    {mode === "review" && offeredResolutions.length >= 2 && (
                                        <div className="import-session-preview__problem-resolution">
                                            <Autocomplete
                                                name={`resolution-${key}`}
                                                options={{
                                                    list: offeredResolutions.map(
                                                        (resolution) => ({
                                                            value: resolution.id,
                                                            label: resolution.label,
                                                        })
                                                    ),
                                                }}
                                                value={resolutions[key] ?? null}
                                                onChange={(resolutionId) =>
                                                    onResolutionChange(
                                                        problem,
                                                        resolutionId ?? ""
                                                    )
                                                }
                                                additionalProps={{
                                                    clearable: true,
                                                    fieldWidth: 360,
                                                    customKey: `resolution-${key}`,
                                                    // Escape the modal's overflow clip &
                                                    // flip upward near the viewport bottom.
                                                    menuPortal: true,
                                                    menuPlacement: "auto",
                                                }}
                                            />
                                        </div>
                                    )}
                                    {mode !== "done" &&
                                        resolutions[key] === CHANGE_VALUE &&
                                        changeValueField(problem) && (
                                            <ChangeValueInput
                                                problem={problem}
                                                inputKey={key}
                                                field={changeValueField(problem) as string}
                                                value={values[key] ?? ""}
                                                readOnly={mode !== "review"}
                                                onChange={(value) =>
                                                    onValueChange?.(problem, value)
                                                }
                                            />
                                        )}
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}

            {isDone && (preview.overridden ?? []).length > 0 && (
                <div className="import-session-preview__overrides">
                    <div className="import-session-preview__section-title">
                        Changed values
                    </div>
                    <ul className="import-session-preview__override-list">
                        {(preview.overridden ?? []).map((entry, index) => (
                            <li
                                // eslint-disable-next-line react/no-array-index-key
                                key={`${entry.record_type}-${entry.record_name}-${entry.field}-${index}`}
                            >
                                {describeOverride(entry)}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {isDone && (preview.disable_reenabled ?? []).length > 0 && (
                <div className="import-session-preview__overrides">
                    <div className="import-session-preview__section-title">
                        Disabled and enabled again
                    </div>
                    <ul className="import-session-preview__override-list">
                        {(preview.disable_reenabled ?? []).map((entry, index) => (
                            <li
                                // eslint-disable-next-line react/no-array-index-key
                                key={`${entry.record_type}-${entry.record_name}-${index}`}
                            >
                                {`${entry.record_type} "${entry.record_name ?? ""}" was disabled, updated and enabled again`}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="import-session-preview__records">
                <div className="import-session-preview__section-title">
                    Records ({records.length + createdLocations.length})
                </div>
                <div className="import-session-preview__table-scroll">
                    <table className="import-session-preview__table">
                        <thead>
                            <tr>
                                <th>Type</th>
                                <th>Name</th>
                                <th>Action</th>
                                <th>{isDone ? "Result" : "Status"}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {records.map((record, index) => {
                                const skipped = record.action === "skip";
                                return (
                                    <tr
                                        // eslint-disable-next-line react/no-array-index-key
                                        key={`${record.record_type}-${record.name}-${index}`}
                                        className={`import-session-preview__row import-session-preview__row--${record.status}`}
                                    >
                                        <td>{record.record_type}</td>
                                        <td>{record.name ?? <i>(unnamed)</i>}</td>
                                        <td>
                                            {record.restored
                                                ? "restore"
                                                : record.action}
                                        </td>
                                        {isDone ? (
                                            <td
                                                className={
                                                    skipped
                                                        ? "import-session-preview__result import-session-preview__result--skipped"
                                                        : "import-session-preview__result import-session-preview__result--imported"
                                                }
                                            >
                                                {skipped ? "— Skipped" : "✓ Imported"}
                                            </td>
                                        ) : (
                                            <td>{record.status}</td>
                                        )}
                                    </tr>
                                );
                            })}
                            {createdLocations.map((location, index) => (
                                <tr
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={`created-${location.record_type}-${location.name}-${index}`}
                                    className="import-session-preview__row import-session-preview__row--ok"
                                >
                                    <td>{location.record_type}</td>
                                    <td>{location.name ?? <i>(unnamed)</i>}</td>
                                    <td>{location.restored ? "restore" : "create"}</td>
                                    <td className="import-session-preview__result import-session-preview__result--created">
                                        {location.restored ? "✓ Restored" : "✓ Created"}
                                    </td>
                                </tr>
                            ))}
                            {records.length === 0 && createdLocations.length === 0 && (
                                <tr>
                                    <td colSpan={4}>
                                        <i>No records found in this file.</i>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default ImportSessionPreview;
